import React, { createContext, useContext, useState, useEffect } from "react";
import { get_user_type, logout as apiLogout } from "../api/AuthApi";
import axios from "axios";
import { Spin } from "antd";

// Create the authentication context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  // Helper function to parse JWT token and get expiry time
  const getTokenExpiry = (token) => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.exp) {
        // Convert expiry time to milliseconds
        return payload.exp * 1000;
      }
    } catch (error) {
      console.error("Error parsing token:", error);
    }
    return null;
  };

  useEffect(() => {
    // Check if user is logged in
    const checkAuthStatus = async () => {
      const token = sessionStorage.getItem("authToken");

      if (token) {
        // Get token expiry time
        const expiry = getTokenExpiry(token);
        setTokenExpiry(expiry);

        try {
          // Fetch user data including role
          const userData = await get_user_type();

          setUser({
            token,
            user_type: userData.user_type,
          });
        } catch (error) {
          console.error("Failed to get user data:", error);
          // Clear access token and set user to null
          sessionStorage.removeItem("authToken");
          setUser(null);
        }
      }

      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = (userData) => {
    // Store in both sessionStorage and localStorage
    sessionStorage.setItem("authToken", userData.access_token);
    // localStorage.setItem("authToken", userData.access_token);

    // Update token expiry
    const expiry = getTokenExpiry(userData.access_token);
    setTokenExpiry(expiry);

    // Store user data in state (without refresh token)
    setUser({
      ...userData,
      token: userData.access_token,
    });
  };

  // Logout function
  const logout = async () => {
    try {
      // Call the API to invalidate the refresh token on the server
      // The server will clear the HTTP-only cookie
      await apiLogout();
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      // Clear both storage locations and reset state
      sessionStorage.removeItem("authToken");
      // localStorage.removeItem("authToken");
      setTokenExpiry(null);
      setUser(null);
    }
  };

  // Check if user has a specific role
  const hasRole = (roles) => {
    if (!user) return false;

    // Handle case where role might be individual characters instead of a string
    let userRole = user.user_type;

    if (Array.isArray(roles)) {
      return roles.includes(userRole);
    }

    return userRole === roles;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    // Derive authentication status from presence of user object
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <Spin spinning={true} fullscreen /> : children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
